import React, { useMemo, useEffect } from 'react'
import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { useMessagesDashboardViewContext } from '../MessagesDashboardViewContext'
import { getMessageStatusColor, getMessageStatusLabel } from 'common/constants'

interface MessageMetricsCounterProps {}

export default function MessageMetricsCounter(props: MessageMetricsCounterProps) {
  const context = useMessagesDashboardViewContext()
  const { traffic } = context

  const metrics = useMemo(() => {
    if (!traffic?.traffic?.buckets?.length) {
      return {
        total: 0,
        processed: 0,
        failed: 0,
        processing: 0,
        ignored: 0,
        created: 0,
        archived: 0,
      }
    }

    const countByStatus = traffic.traffic.buckets.reduce((acc, bucket) => {
      Object.keys(bucket.per_agg).forEach(status => {
        if (!acc[status]) {
          acc[status] = 0
        }
        acc[status] += bucket.per_agg[status]
      })
      return acc
    }, {} as Record<string, number>)

    return {
      total: traffic.traffic.count,
      processed: countByStatus.processed || 0,
      failed: countByStatus.failed || 0,
      processing: countByStatus.processing || 0,
      ignored: countByStatus.ignored || 0,
      created: countByStatus.created || 0,
      archived: countByStatus.archived || 0,
    }
  }, [traffic])

  // Only show metrics that have values > 0
  const activeMetrics = Object.entries(metrics).filter(
    ([key, value]) => key !== 'total' && value > 0
  )

  // For mobile, only show critical metrics
  const criticalMetrics = activeMetrics.filter(([status]) =>
    ['failed', 'processed'].includes(status)
  )

  const handleMetricClick = (status: string) => {
    // Update the status filter to show only messages with this status
    context.filters.updateField('status', [
      {
        description: status,
        status: status,
        _type: 'string',
      },
    ])
  }

  // Use useEffect to trigger search when filters change
  useEffect(() => {
    // Only trigger search if we have status filters (indicating a metric was clicked)
    if (context.filters.fields.status && context.filters.fields.status.length > 0) {
      const timer = setTimeout(() => {
        context.searchMessagesTraffic()
        context.searchMessages()
      }, 200)

      return () => clearTimeout(timer)
    }
  }, [context.filters.fields.status])

  if (!traffic?.traffic?.buckets?.length) {
    return null
  }

  return (
    <>
      {/* Desktop version */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          // Responsive design - hide on very small screens
          '@media (max-width: 600px)': {
            display: 'none',
          },
        }}
      >
        {activeMetrics.map(([status, count]) => (
          <Tooltip
            key={status}
            title={`Click to filter by ${getMessageStatusLabel(status)} status`}
            arrow
          >
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: getMessageStatusColor(status),
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {count.toLocaleString()}
                  </Typography>
                </Box>
              }
              size="small"
              onClick={() => handleMetricClick(status)}
              sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                // Compact on medium screens
                '@media (max-width: 900px)': {
                  '& .MuiChip-label': {
                    fontSize: '0.75rem',
                  },
                },
              }}
            />
          </Tooltip>
        ))}
      </Box>

      {/* Mobile version - only show critical metrics */}
      <Box
        sx={{
          display: 'none',
          alignItems: 'center',
          gap: 0.5,
          '@media (max-width: 600px)': {
            display: 'flex',
          },
        }}
      >
        {criticalMetrics.map(([status, count]) => (
          <Tooltip
            key={status}
            title={`Click to filter by ${getMessageStatusLabel(status)} status`}
            arrow
          >
            <Box
              onClick={() => handleMetricClick(status)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.5,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                fontSize: '0.7rem',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: getMessageStatusColor(status),
                }}
              />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                {count.toLocaleString()}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </>
  )
}
